import { useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getSorAccessToken,
  renewSorToken,
  setSorToken,
  type SorTokenProvider,
} from '../sor/sorTokenStore';

/**
 * Build-time defaults. These resolve in apps that compile this source with envPrefix 'CTY_' (doc-web);
 * in the published package `import.meta.env` is empty, so apps consuming the package (e.g. PAL)
 * should call configureSorApi() at startup.
 */
// No default: a build without CTY_SOR_AUDIENCE must not silently call another environment's SOR
const SOR_URL: string = String(import.meta.env.CTY_SOR_AUDIENCE ?? '').trim();
// Auth0 API identifier for SOR; the same as the SOR app URL
const SOR_AUDIENCE: string = SOR_URL;

export interface SorApiConfig {
  /** SOR base URL, e.g. https://dev.sor.circuitry.ai */
  baseUrl: string;
  /** Auth0 API identifier (audience) for SOR; defaults to baseUrl */
  audience: string;
}

let sorConfig: SorApiConfig = { baseUrl: SOR_URL, audience: SOR_AUDIENCE };

/**
 * Overrides the SOR base URL / audience for every useSorApi call. Call once before rendering,
 * e.g. configureSorApi({ baseUrl: import.meta.env.CTY_SOR_URL }) in an app using the published package.
 */
export function configureSorApi(config: Partial<SorApiConfig>): void {
  let baseUrl = config.baseUrl || sorConfig.baseUrl;
  // Drop trailing slashes (a loop instead of a backtracking-prone regex)
  while (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);
  sorConfig = {
    baseUrl,
    audience:
      config.audience || (config.baseUrl ? baseUrl : sorConfig.audience),
  };
}

export function getSorApiConfig(): Readonly<SorApiConfig> {
  return sorConfig;
}

/** The SOR config, or a SorAuthError when neither CTY_SOR_AUDIENCE nor configureSorApi() set it. */
function requireSorConfig(): Readonly<SorApiConfig> {
  if (!sorConfig.baseUrl || !sorConfig.audience) {
    throw new SorAuthError(
      'sor_not_configured',
      'The System of Record is not configured for this environment (CTY_SOR_AUDIENCE is not set).'
    );
  }
  return sorConfig;
}

// Shared across hook instances so only one consent popup is open at a time
let pendingPopupToken: Promise<string> | null = null;

/** One condition; `field` is a record path such as `data.title` */
export interface SorFilterCondition {
  field: string;
  op: string;
  value?: unknown;
}

/** Filter tree accepted by POST /api/v1/records/query */
export type SorFilter =
  | SorFilterCondition
  | { and: SorFilter[] }
  | { or: SorFilter[] }
  | { not: SorFilter };

/** null when there are no conditions, the condition itself when there's one, otherwise an `and` group */
export function combineSorFilters(
  conditions: (SorFilter | null | undefined | false)[]
): SorFilter | null {
  const active = conditions.filter(
    (condition): condition is SorFilter => !!condition
  );
  if (active.length === 0) return null;
  if (active.length === 1) return active[0];
  return { and: active };
}

/** One sort key; `field` may follow a relation, e.g. `source.created_at` */
export interface SorSortSpec {
  field: string;
  dir?: 'asc' | 'desc';
  nulls?: 'first' | 'last';
}

/** A facet to count by; `limit` is the number of buckets (1-100, default 20) */
export interface SorFacetSpec {
  field: string;
  limit?: number;
}

export interface SorQueryPayload {
  definition: string;
  filter?: SorFilter | null;
  search?: string | null;
  state?: string | null;
  sort?: SorSortSpec[];
  include_deleted?: boolean;
  /** Reference fields to resolve into each item's `expanded`, e.g. ['definition'] */
  expand?: string[];
  /** Related records to load into each item's `related`, e.g. ['roles'] */
  include?: string[];
  /**
   * Fields to return instead of the whole record, e.g. ['id', 'data.queueId', 'source.state'].
   * Own fields come back flat (`item['data.queueId']`); relation paths come back under
   * `item.related.<relation>` with the rest of the path as the key (`related.source['customer.name']`).
   */
  select?: string[];
  /** Counts per value of each field, returned in `facets`; a facet ignores its own filter condition */
  facets?: SorFacetSpec[];
  /** `cursor` (from `next_cursor`) pages the default order; `offset` is for an explicit `sort` */
  page?: { limit: number; offset?: number; cursor?: string | null };
}

/** A stored SOR record; `data` holds the definition-specific fields */
export interface SorRecord<TData = Record<string, unknown>> {
  id: string;
  definition: string;
  definition_version: number;
  external_id: string | null;
  natural_key: string | null;
  display: string | null;
  state: string | null;
  revision: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  data: TData;
}

export interface SorFacetBucket {
  value: unknown;
  count: number;
}

/** `capped` means there were more values than the facet's `limit` */
export interface SorFacet {
  buckets: SorFacetBucket[];
  capped: boolean;
}

export interface SorQueryResponse<TData = Record<string, unknown>> {
  items: SorRecord<TData>[];
  total: number;
  limit: number;
  offset: number;
  total_capped: boolean;
  next_cursor: string | null;
  /** Set when the query asked for `facets`, keyed by field */
  facets?: Record<string, SorFacet> | null;
}

/** One problem reported in an error response, e.g. { code: 'assignment_prepared', source: 'RULE' } */
export interface SorErrorDetail {
  path?: string;
  message?: string;
  code?: string;
  source?: string;
  severity?: string;
}

/**
 * A non-2xx SOR response. `code` is the API's error code (e.g. `revision_conflict`, `validation_failed`,
 * `invalid_query`); the message keeps the "<status> <statusText>: <body>" format callers already log.
 */
export class SorApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: SorErrorDetail[];
  readonly meta: Record<string, unknown> | null;
  /** The API's own message, e.g. "Expected revision 1 but the record is at 17" */
  readonly apiMessage: string | null;

  constructor(status: number, statusText: string, body: string) {
    super(`${status} ${statusText}${body ? ': ' + body : ''}`);
    this.name = 'SorApiError';
    this.status = status;
    let parsed: { code?: string; message?: string; details?: SorErrorDetail[]; meta?: Record<string, unknown> } | null = null;
    try {
      parsed = body ? JSON.parse(body) : null;
    } catch {
      // Not JSON (e.g. a gateway error page)
    }
    this.code = parsed?.code ?? null;
    this.apiMessage = parsed?.message ?? null;
    this.details = Array.isArray(parsed?.details) ? parsed.details : [];
    this.meta = parsed?.meta ?? null;
  }
}

export function isSorApiError(error: unknown): error is SorApiError {
  return (
    error instanceof SorApiError ||
    (error as { name?: string } | null)?.name === 'SorApiError'
  );
}

/**
 * Thrown when no SOR access token could be obtained (consent/login required, popup blocked or closed).
 * Recover by calling `connectSor()` from a click handler, then retry.
 */
export class SorAuthError extends Error {
  readonly code: string;

  constructor(code: string, message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = 'SorAuthError';
    this.code = code;
    if (options?.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.cause;
    }
  }
}

export function isSorAuthError(error: unknown): error is SorAuthError {
  return (
    error instanceof SorAuthError ||
    (error as { name?: string } | null)?.name === 'SorAuthError'
  );
}

function toSorAuthError(err: unknown): SorAuthError {
  const code = (err as { error?: string } | null)?.error ?? 'token_unavailable';
  const detail = err instanceof Error && err.message ? ` (${err.message})` : '';
  return new SorAuthError(
    code,
    `Could not get access to the System of Record${detail}.`,
    { cause: err }
  );
}

// Auth0 errors that only an interactive sign-in can resolve
const INTERACTIVE_AUTH_CODES = new Set([
  'consent_required',
  'login_required',
  'interaction_required',
]);

type Auth0Session = Pick<
  ReturnType<typeof useAuth0>,
  'user' | 'getAccessTokenSilently'
>;

/** The Auth0 organization of the signed-in user */
export function sorOrganization(user: Auth0Session['user']): string | undefined {
  return user?.org_id ?? user?.['https://circuitry.ai/org_id'];
}

/** Fetches the SOR token silently from the app's existing Auth0 session. */
export function createAuth0SorTokenProvider({
  user,
  getAccessTokenSilently,
}: Auth0Session): SorTokenProvider {
  return createOrganizationSorTokenProvider(sorOrganization(user), getAccessTokenSilently);
}

function createOrganizationSorTokenProvider(
  organization: string | undefined,
  getAccessTokenSilently: Auth0Session['getAccessTokenSilently']
): SorTokenProvider {
  return async ({ forceRefresh }) => {
    const { audience } = requireSorConfig();
    try {
      return await getAccessTokenSilently({
        authorizationParams: { audience, organization },
        cacheMode: forceRefresh ? 'off' : 'on',
      });
    } catch (err) {
      throw toSorAuthError(err);
    }
  };
}

/**
 * Calls the SOR API with the app-wide SOR token (see sorTokenStore), which is renewed when it
 * expires, so getSorToken is cheap to call before every request.
 */
export function useSorApi() {
  const { user, getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const organization = sorOrganization(user);

  // Used when no app-wide provider is set, e.g. before the tenant settings load. Keyed on the
  // organization, not `user`: with a localstorage cache Auth0 hands back a new `user` object after
  // every token fetch, which would change queryRecords and re-run every effect that depends on it
  const silentToken = useMemo(
    () => createOrganizationSorTokenProvider(organization, getAccessTokenSilently),
    [organization, getAccessTokenSilently]
  );

  /** Interactive consent/login for the SOR audience. Call only from a click handler so the popup isn't blocked. */
  const connectSor = useCallback(async () => {
    try {
      const authorizationParams = { audience: requireSorConfig().audience, organization };
      const token = await getAccessTokenWithPopup({ authorizationParams });
      if (!token) {
        throw new SorAuthError(
          'popup_cancelled',
          'Sign-in to the System of Record was cancelled.'
        );
      }
      setSorToken(token);
      return token;
    } catch (err) {
      throw isSorAuthError(err) ? err : toSorAuthError(err);
    }
  }, [getAccessTokenWithPopup, organization]);

  const getSorToken = useCallback(async () => {
    try {
      return await getSorAccessToken(silentToken);
    } catch (err) {
      if (!isSorAuthError(err) || !INTERACTIVE_AUTH_CODES.has(err.code)) throw err;
      // Browsers block this popup unless it follows a user action; callers then show a
      // "Connect" button that calls connectSor() from the click. Without the shared promise,
      // concurrent callers each open a popup and the second fails with "Invalid state"
      pendingPopupToken ??= connectSor().finally(() => {
        pendingPopupToken = null;
      });
      return pendingPopupToken;
    }
  }, [silentToken, connectSor]);

  const sorFetch = useCallback(
    async <T = unknown>(path: string, init: RequestInit = {}): Promise<T> => {
      const send = (token: string) =>
        fetch(`${requireSorConfig().baseUrl}${path}`, {
          ...init,
          headers: {
            'Content-Type': 'application/json',
            ...init.headers,
            Authorization: `Bearer ${token}`,
          },
        });
      const token = await getSorToken();
      let response = await send(token);
      // The token expired or was revoked before its expiry; retry once with a new one
      if (response.status === 401) {
        response = await send(await renewSorToken(token, silentToken));
      }

      if (!response.ok) {
        throw new SorApiError(
          response.status,
          response.statusText,
          await response.text()
        );
      }
      // DELETE returns 204 with no body
      const body = await response.text();
      return (body ? JSON.parse(body) : undefined) as T;
    },
    [getSorToken, silentToken]
  );

  const queryRecords = useCallback(
    <T = unknown>(payload: SorQueryPayload, signal?: AbortSignal) =>
      sorFetch<T>('/api/v1/records/query', {
        method: 'POST',
        body: JSON.stringify(payload),
        signal,
      }),
    [sorFetch]
  );

  return { connectSor, sorFetch, queryRecords, getSorToken };
}
